import DirectionForward from '@clearkit/icons/glyphs/DirectionForward';
import EnrichmentBlock from '@clearkit/icons/glyphs/EnrichmentBlock';
import RestartIcon from '@clearkit/icons/glyphs/Restart';
import Trash from '@clearkit/icons/glyphs/Trash';
import Clearbit from '@clearkit/icons/services/Clearbit';
import HubSpot from '@clearkit/icons/services/Hubspot';
import Marketo from '@clearkit/icons/services/Marketo';
import Salesforce from '@clearkit/icons/services/Salesforce';
import { CKAttributeSelector, CKButton } from '@clearkit/react';
import { useState } from 'react';

import { isTypeMismatch } from '~/lib/isTypeMismatch';
import { findAttribute } from '~lib/dataEnrichment';

export const RefreshButton = ({ onClickRefresh }) => {
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);

    try {
      await onClickRefresh();
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  };

  return (
    <CKButton
      variant={CKButton.Variant.tertiary}
      variantColor={CKButton.VariantColor.blue}
      disabled={loading}
      onClick={onClick}
      leftIcon={<RestartIcon />}>
      {loading ? 'Refreshing' : 'Refresh'}
    </CKButton>
  );
};

export default function FormFieldMappingRow({
  mapping,
  service,
  onChange,
  onRemove,
  sourceAttributes,
  targetAttributes,
  onClickRefreshAttributes,
  sourceErrors,
  targetErrors,
  showError,
}) {
  const mappingToAttribute = (mappingValue) => {
    if (!mappingValue) return null;

    const [sourceType, objectType, ...value] = mappingValue.split('.');

    return {
      sourceType,
      objectType,
      value: value.join('.'),
    };
  };

  const mappingAttributeToValue = (mappingAttribute) => {
    const heirarchy = [];
    heirarchy.push(mappingAttribute.meta.sourceType);
    heirarchy.push(mappingAttribute.meta.objectType);
    heirarchy.push(mappingAttribute.value);
    return heirarchy.join('.');
  };

  const selectedSourceAttribute = sourceAttributes
    ? findAttribute(
        mappingToAttribute(mapping.sourceAttribute),
        sourceAttributes,
      )
    : null;
  const selectedTargetAttribute = targetAttributes
    ? findAttribute(
        mappingToAttribute(mapping.targetAttribute),
        targetAttributes,
      )
    : null;

  const inErrorState = (sourceOrTarget) => {
    if (sourceOrTarget === 'source') {
      return !!sourceErrors || (!mapping.sourceAttribute && showError);
    } else if (sourceOrTarget === 'target') {
      return !!targetErrors || (!mapping.targetAttribute && showError);
    } else return false;
  };

  const renderServiceIcon = (service) => {
    switch (service) {
      case 'HubSpot':
        return <HubSpot width={18} height={18} />;
      case 'Marketo':
        return <Marketo width={18} height={18} />;
      case 'Pardot':
        return <Salesforce width={18} height={18} />;
      default:
        return <EnrichmentBlock width={18} height={18} />;
    }
  };

  const rootCategoryIdentifier = (service) => {
    switch (service) {
      case 'HubSpot':
        return 'category_hubspot';
      case 'Marketo':
        return 'category_marketo';
      case 'Pardot':
        return 'category_pardot';
      default:
        return 'category_enrichment';
    }
  };

  return (
    <div className="forms-mapping-row-grid">
      <CKAttributeSelector
        variant={inErrorState('source') && 'error'}
        icon={<Clearbit width={18} height={18} />}
        attributes={sourceAttributes || null}
        isTypeMismatch={(attribute) =>
          isTypeMismatch(attribute, selectedTargetAttribute)
        }
        onSelectedItemChange={(selected) =>
          onChange({
            sourceAttribute: mappingAttributeToValue(selected),
          })
        }
        placeholder="Find an attribute"
        selectedAttribute={selectedSourceAttribute}
        popperZIndex={30}
      />

      <DirectionForward className="text-gray-400" />

      <CKAttributeSelector
        variant={inErrorState('target') && 'error'}
        icon={renderServiceIcon(service)}
        attributes={targetAttributes || null}
        isTypeMismatch={(attribute) =>
          isTypeMismatch(selectedSourceAttribute, attribute)
        }
        onSelectedItemChange={(selected) =>
          onChange({
            targetAttribute: mappingAttributeToValue(selected),
          })
        }
        rootCategoryIdentifier={rootCategoryIdentifier(service)}
        rootCategoryLabel={service}
        rootCategoryMeta={
          <RefreshButton onClickRefresh={onClickRefreshAttributes} />
        }
        placeholder={`Choose a ${service} attribute`}
        selectedAttribute={selectedTargetAttribute}
        detailDrawerPosition="left"
        popperZIndex={30}
      />

      <CKButton onClick={onRemove} isSmall variant="tertiary">
        <Trash className="text-gray-400" />
      </CKButton>
    </div>
  );
}
